import React, { useState } from 'react';
import { ArrowLeft, Mail } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
} from '../components/ui/card';
import { useTranslation } from '../hooks/useTranslation';
import { useNotification } from '../hooks/useNotification';

interface ForgotPasswordProps {
  onBack?: () => void;
}

const ForgotPassword: React.FC<ForgotPasswordProps> = ({ onBack }) => {
  const { translate } = useTranslation();
  const { notify } = useNotification();
  const [email, setEmail] = useState('');
  const [isLoading, setIsLoading] = useState(false);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsLoading(true);

    try {
      // TODO: Implement forgot password functionality
      await new Promise((resolve) => setTimeout(resolve, 1000)); // Simulate API call

      notify('Password reset instructions sent to your email', {
        type: 'success',
      });

      setEmail('');
    } catch (error) {
      notify('Failed to send password reset email', {
        type: 'error',
      });
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <Card className="w-full max-w-md mx-auto">
      <CardHeader className="space-y-1">
        <div className="flex items-center gap-2">
          {onBack && (
            <Button variant="ghost" size="sm" onClick={onBack} className="p-1">
              <ArrowLeft className="h-4 w-4" />
            </Button>
          )}
          <CardTitle className="text-2xl">
            {translate('pos.auth.forgotPassword')}
          </CardTitle>
        </div>
        <p className="text-sm text-muted-foreground">
          {translate('pos.auth.forgotPasswordDescription')}
        </p>
      </CardHeader>
      <CardContent>
        <form onSubmit={handleSubmit} className="space-y-4">
          <div className="space-y-2">
            <Label htmlFor="email">{translate('pos.auth.email')}</Label>
            <div className="relative">
              <Mail className="absolute left-3 top-3 h-4 w-4 text-muted-foreground" />
              <Input
                id="email"
                type="email"
                placeholder={translate('pos.auth.emailPlaceholder')}
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className="pl-10"
                required
              />
            </div>
          </div>

          <Button
            type="submit"
            className="w-full"
            disabled={isLoading || !email}
          >
            {isLoading
              ? translate('pos.auth.sending')
              : translate('pos.auth.sendResetLink')}
          </Button>
        </form>
      </CardContent>
    </Card>
  );
};

export default ForgotPassword;
